import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { METHANOL_RESIDUAL_MODEL } from "@brewbase/lib";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { formatDateTime } from "@/lib/date";
import { cn } from "@/lib/utils";
import { RESPONSE_LABEL, feedStopLabel } from "@/features/fermentation-live/methanol-detector-labels";

export const Route = createFileRoute("/fermentations/methanol-suggestions")({
  component: MethanolSuggestionsPage,
  head: () => ({ meta: [{ title: "BrewBase - Methanol suggestions" }] }),
});

type Suggestion = RouterOutputs["methanolSuggestions"]["review"][number];
type Outcome = "confirmed" | "low" | "not_found" | "stop_delayed" | "stop_fast" | "incident" | "none";
type FeedStop = Suggestion["followUp"]["feedStops"][number];

const OUTCOME_LABEL: Record<Outcome, string> = {
  confirmed: `Methanol ≥${METHANOL_RESIDUAL_MODEL.targetVolPct}`,
  low: "Methanol 0.02–0.09",
  not_found: "Methanol below 0.02",
  stop_delayed: "Build-up at feed stop",
  stop_fast: "No build-up at feed stop",
  incident: "Incident reported",
  none: "No follow-up",
};

const OUTCOME_CLASS: Record<Outcome, string> = {
  confirmed: "bg-green-3 text-green-11",
  low: "bg-yellow-3 text-yellow-11",
  not_found: "bg-olive-3 text-olive-11",
  stop_delayed: "bg-green-3 text-green-11",
  stop_fast: "bg-olive-3 text-olive-11",
  incident: "bg-green-3 text-green-11",
  none: "text-olive-9",
};

/**
 * What followed. The first methanol measurement within 6 h decides; without
 * one, the first readable feed stop (it says whether methanol was left over,
 * not how much); then an incident.
 */
function outcomeOf(s: Suggestion): Outcome {
  const v = s.followUp.sample?.volPct;
  if (v !== undefined) return v >= METHANOL_RESIDUAL_MODEL.targetVolPct ? "confirmed" : v >= 0.02 ? "low" : "not_found";
  const stop = s.followUp.feedStops.find((f) => f.target !== null);
  if (stop) return stop.target === 1 ? "stop_delayed" : "stop_fast";
  return s.followUp.incidents > 0 ? "incident" : "none";
}

const PERIODS = [7, 30, 90];

/**
 * Every methanol suggestion the live model raised, with the operator's answer
 * and what followed within 6 h. This is how the model is judged in use.
 */
function MethanolSuggestionsPage() {
  const [days, setDays] = useState(7);
  const { data, isLoading } = trpc.methanolSuggestions.review.useQuery({ days });
  const counts = useMemo(() => {
    const out: Record<Outcome, number> = { confirmed: 0, low: 0, not_found: 0, stop_delayed: 0, stop_fast: 0, incident: 0, none: 0 };
    for (const s of data ?? []) out[outcomeOf(s)]++;
    return out;
  }, [data]);
  const answered = (data ?? []).filter((s) => s.response !== null).length;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-medium">Methanol suggestions</h1>
          <p className="text-xs text-muted-foreground">
            What the live model suggested on continuous tanks, how operators answered, and what the next methanol measurement showed
          </p>
        </div>
        <div className="flex items-center gap-1">
          {PERIODS.map((d) => (
            <Button
              key={d}
              variant="ghost"
              size="sm"
              onClick={() => setDays(d)}
              className={cn(
                "h-7 px-2 text-xs",
                days === d ? "bg-primary/10 text-primary hover:bg-primary/15" : "text-muted-foreground",
              )}
            >
              {d} days
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data?.length ?? 0) === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">No suggestions in this period.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span>
              <span className="font-mono">{data!.length}</span> suggestions, <span className="font-mono">{answered}</span>{" "}
              answered
            </span>
            {(Object.keys(OUTCOME_LABEL) as Outcome[]).map((o) => (
              <span key={o}>
                {OUTCOME_LABEL[o]}: <span className="font-mono">{counts[o]}</span>
              </span>
            ))}
          </div>
          <table className="w-full text-sm border border-olive-6">
            <thead>
              <tr className="text-xs text-olive-11 bg-olive-2 border-b border-olive-6">
                <th className="text-left font-medium px-2 py-1.5 w-36">Raised</th>
                <th className="text-left font-medium px-2 py-1.5 w-28">Tank</th>
                <th className="text-left font-medium px-2 py-1.5 w-28">Peak chance</th>
                <th className="text-left font-medium px-2 py-1.5">Answer</th>
                <th className="text-left font-medium px-2 py-1.5 w-40">Next methanol (6 h)</th>
                <th className="text-left font-medium px-2 py-1.5 w-56">Feed-stop check</th>
                <th className="text-left font-medium px-2 py-1.5 w-36">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {data!.map((s) => {
                const outcome = outcomeOf(s);
                return (
                  <tr key={s.id} className="border-b border-olive-5 hover:bg-olive-3 align-top">
                    <td className="px-2 py-1.5">
                      {formatDateTime(s.raisedAt)}
                      {s.clearedAt === null && <span className="ml-1 text-xs text-yellow-11">open</span>}
                    </td>
                    <td className="px-2 py-1.5">{s.fermentationName ?? `#${s.fermentationId}`}</td>
                    <td className="px-2 py-1.5 font-mono">{Math.round(s.peakPTarget * 100)}%</td>
                    <td className="px-2 py-1.5">
                      {s.response ? RESPONSE_LABEL[s.response] : <span className="text-olive-9">—</span>}
                      {s.respondedBy && <span className="ml-1 text-xs text-olive-9">{s.respondedBy}</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {s.followUp.sample ? (
                        <>
                          <span className="font-mono">{s.followUp.sample.volPct}</span> vol%{" "}
                          <span className="text-xs text-olive-9">{formatDateTime(s.followUp.sample.at)}</span>
                        </>
                      ) : (
                        <span className="text-olive-9">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <FeedStops stops={s.followUp.feedStops} />
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={cn("px-1 text-xs", OUTCOME_CLASS[outcome])}>{OUTCOME_LABEL[outcome]}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground">
            Model {METHANOL_RESIDUAL_MODEL.version}.{" "}
            <Link to="/fermentations/investigations" className="hover:underline">
              Incidents
            </Link>{" "}
            count only when reported with the methanol accumulation category.
          </p>
        </>
      )}
    </div>
  );
}

function FeedStops({ stops }: { stops: FeedStop[] }) {
  const shown = stops.flatMap((f) => {
    const label = feedStopLabel(f);
    return label ? [{ f, label }] : [];
  });
  if (shown.length === 0) return <span className="text-olive-9">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {shown.map(({ f, label }) => (
        <span key={f.commandAt.toISOString()} className="text-sm">
          <span className="text-xs text-olive-9">{format(f.commandAt, "HH:mm")}</span>{" "}
          <span className={label.className}>{label.text}</span>
        </span>
      ))}
    </div>
  );
}
